
import {
  PostApi,
  describePostPublishError,
  PublishPostOptions,
} from '@/api/PostApi';
import { GoogleMyBusinessApi } from '@/api/GoogleMyBusinessApi';
import { InstagramApi } from '@/api/InstagramApi';
import { LinkedInApi } from '@/api/LinkedInApi';
import PostMediaDropzone, {
  PostMediaInputPayload,
} from '@/components/post/PostMediaDropzone.vue';
import ImportPostsModal, { ImportFeedItem } from '@/components/post/ImportPostsModal.vue';
import {
  GENERIC_ERROR_DESCRIPTION,
  GOOGLE_BUSINESS_MAX_VIDEO_DURATION_SECONDS,
  SOCIAL_PLATFORMS_METADATA,
} from '@/constants';
import { AppRoute } from '@/router/routes';
import { CrossPostDraft, takeCrossPostDraft } from '@/utils/crossPostDraft';
import { extractApiErrorMessage } from '@/utilities';
import { Bot, Chain, DEFAULT_TIMEZONE, PublishPost, SupportedPlatform } from 'ignite360-core';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { RawLocation } from 'vue-router';

/** True when Google Business has a linked location (import does not require posting to be enabled). */
function isGoogleBusinessConnectedForProject(bot: Bot | null | undefined): boolean {
  if (!bot?.platformData?.googleBusiness) {
    return false;
  }
  const gb = bot.platformData.googleBusiness;
  return !!(gb.accountId && gb.locationId && !gb.isExpired);
}

function isGoogleBusinessPostingEnabledForProject(bot: Bot | null | undefined): boolean {
  return (
    isGoogleBusinessConnectedForProject(bot) && !!bot?.platformData?.googleBusiness?.enablePosting
  );
}

/** True when the project (bot) has a linked Instagram Business account and tokens to call the Graph API. */
function isInstagramAccountConnectedForProject(bot: Bot | null | undefined): boolean {
  if (!bot?.platformData?.instagram) {
    return false;
  }
  const ig = bot.platformData.instagram;
  return !!(
    ig.pageId &&
    ig.pageAccessToken &&
    ig.pageName &&
    ig.businessAccountId &&
    ig.userAccessToken
  );
}

function isInstagramPostingEnabledForProject(bot: Bot | null | undefined): boolean {
  return (
    isInstagramAccountConnectedForProject(bot) && !!bot?.platformData?.instagram?.enablePosting
  );
}

function isLinkedInConnectedForProject(bot: Bot | null | undefined): boolean {
  return !!(bot?.platformData?.linkedIn?._id && bot?.platformData?.linkedIn?.type);
}

function isLinkedInPostingEnabledForProject(bot: Bot | null | undefined): boolean {
  return isLinkedInConnectedForProject(bot) && !!bot?.platformData?.linkedIn?.enablePosting;
}

function isFacebookConnectedForProject(bot: Bot | null | undefined): boolean {
  const fb = bot?.platformData?.facebookMessenger;
  return !!(fb?.pageId && fb?.pageAccessToken && fb?.pageName);
}

function isFacebookPostingEnabledForProject(bot: Bot | null | undefined): boolean {
  return (
    isFacebookConnectedForProject(bot) && !!bot?.platformData?.facebookMessenger?.enablePosting
  );
}

function findBotForImport(bots: Bot[], isConnected: (bot: Bot) => boolean): Bot | null {
  return bots.find((bot) => isConnected(bot)) || null;
}

@Component({
  name: 'publish-post-card',
  components: { PostMediaDropzone, ImportPostsModal },
})
export default class PublishPostCard extends Vue {
  @Prop({ required: false, type: Object })
  readonly chain!: Chain;

  @Prop({ required: false, type: Object })
  readonly bot!: Bot;

  @Prop({ required: true, type: String })
  readonly type!: 'chain' | 'bot';

  postData = this.resetValue();
  postImageHorizontal: File | null = null;
  postImageSquare: File | null = null;
  postVideo: File | null = null;
  /** Duration in seconds for the uploaded video preview, when available. */
  videoDurationSeconds: number | null = null;
  showImportModal = false;
  mediaDropzoneKey = 0;
  /** Temporary UI toggle: keep CTA payload logic, hide CTA inputs. */
  showCallToAction = false;

  selectedBots: Bot[] = [];
  selectedPlatforms: SupportedPlatform[] = [];

  isSubmitting = false;
  /** Keeps Feed cross-post prefill from being wiped by bot/platform watchers. */
  private preserveCrossPostPrefill = false;

  /** Candidate bots used to calculate enabled social posting platforms. */
  get botsInScope(): Bot[] {
    if (this.type === 'bot') {
      return this.bot ? [this.bot] : [];
    }
    return this.selectedBots.length ? this.selectedBots : this.chain?.bots || [];
  }

  /** Link to project social posting integrations (bot compose only). */
  get socialPostingIntegrationsRoute(): RawLocation | null {
    if (this.type !== 'bot' || !this.bot?.id) {
      return null;
    }
    return {
      name: AppRoute.ProjectIntegrationSocialPosting,
      params: { id: this.bot.id },
    };
  }

  /** Platform list shown in picker (restricted to post posting platforms). */
  get enabledPostingPlatforms(): Array<{ value: SupportedPlatform; label: string; image: string }> {
    const allowed = new Set<SupportedPlatform>([
      SupportedPlatform.FacebookMessenger,
      SupportedPlatform.GoogleBusiness,
      SupportedPlatform.LinkedIn,
      SupportedPlatform.Instagram,
      SupportedPlatform.AppleBusinessConnect,
    ]);
    return SOCIAL_PLATFORMS_METADATA.filter(
      (meta) =>
        allowed.has(meta.value as SupportedPlatform) &&
        this.botsInScope.some((bot) => !!meta.isConnected?.(bot) && !meta.isDisabled?.(bot)),
    ).map((meta) => ({
      value: meta.value as SupportedPlatform,
      label: meta.label,
      image: meta.image || '',
    }));
  }

  /** True when a selected video exceeds the Google Business duration limit. */
  get showGoogleBusinessVideoDurationWarning(): boolean {
    return (
      !!this.postVideo &&
      this.isPlatformSelected(SupportedPlatform.GoogleBusiness) &&
      this.videoDurationSeconds != null &&
      this.videoDurationSeconds > GOOGLE_BUSINESS_MAX_VIDEO_DURATION_SECONDS
    );
  }

  get googleBusinessMaxVideoDurationSeconds(): number {
    return GOOGLE_BUSINESS_MAX_VIDEO_DURATION_SECONDS;
  }

  /** Passed to the trim modal when Google My Business is selected. */
  get videoTrimMaxSegmentSeconds(): number | null {
    return this.isPlatformSelected(SupportedPlatform.GoogleBusiness)
      ? GOOGLE_BUSINESS_MAX_VIDEO_DURATION_SECONDS
      : null;
  }

  /** Human-readable duration for warning copy (e.g. 1:30). */
  get formattedVideoDuration(): string {
    if (this.videoDurationSeconds == null) {
      return '';
    }
    const totalSeconds = Math.ceil(this.videoDurationSeconds);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0) {
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }
    return `${seconds}s`;
  }

  get canSubmit(): boolean {
    if (!this.selectedPlatforms.length) {
      return false;
    }
    const hasRequiredText = this.showTitleField
      ? !!this.postData.title && !!this.postData.content
      : !!this.postData.content;
    return (
      hasRequiredText &&
      this.selectedPlatforms.every((platform) => this.platformMediaRequirementMet(platform))
    );
  }

  /** Validates media per platform — Instagram needs an image; video-only is fine for Google/LinkedIn. */
  platformMediaRequirementMet(platform: SupportedPlatform): boolean {
    if (platform === SupportedPlatform.Instagram) {
      return !!(this.postImageHorizontal && this.postImageSquare);
    }
    return true;
  }

  get id(): string {
    return this.type === 'chain' ? this.chain.id : this.bot.id;
  }

  /** First bot in scope that has a usable Instagram link (for reading posts). */
  get instagramLinkBot(): Bot | null {
    return findBotForImport(this.botsInScope, isInstagramAccountConnectedForProject);
  }

  get linkedInLinkBot(): Bot | null {
    return findBotForImport(this.botsInScope, isLinkedInConnectedForProject);
  }

  get facebookLinkBot(): Bot | null {
    return findBotForImport(this.botsInScope, isFacebookConnectedForProject);
  }

  get googleLinkBot(): Bot | null {
    return findBotForImport(this.botsInScope, isGoogleBusinessConnectedForProject);
  }

  /** Platforms connected for import but with posting disabled in Social Posting Integrations. */
  get importPostingDisabledByPlatform(): Partial<Record<SupportedPlatform, boolean>> {
    const map: Partial<Record<SupportedPlatform, boolean>> = {};
    if (this.instagramLinkBot && !isInstagramPostingEnabledForProject(this.instagramLinkBot)) {
      map[SupportedPlatform.Instagram] = true;
    }
    if (this.linkedInLinkBot && !isLinkedInPostingEnabledForProject(this.linkedInLinkBot)) {
      map[SupportedPlatform.LinkedIn] = true;
    }
    if (this.facebookLinkBot && !isFacebookPostingEnabledForProject(this.facebookLinkBot)) {
      map[SupportedPlatform.FacebookMessenger] = true;
    }
    if (this.googleLinkBot && !isGoogleBusinessPostingEnabledForProject(this.googleLinkBot)) {
      map[SupportedPlatform.GoogleBusiness] = true;
    }
    return map;
  }

  get importBotIdsByPlatform(): Partial<Record<SupportedPlatform, string>> {
    return {
      [SupportedPlatform.Instagram]: this.instagramLinkBot?.id || '',
      [SupportedPlatform.LinkedIn]: this.linkedInLinkBot?.id || '',
      [SupportedPlatform.FacebookMessenger]: this.facebookLinkBot?.id || '',
      [SupportedPlatform.GoogleBusiness]: this.googleLinkBot?.id || '',
    };
  }

  get importTimezone(): string {
    return this.botsInScope[0]?.timezone || DEFAULT_TIMEZONE;
  }

  get importablePlatforms(): SupportedPlatform[] {
    const rows: SupportedPlatform[] = [];
    if (this.instagramLinkBot) {
      rows.push(SupportedPlatform.Instagram);
    }
    if (this.linkedInLinkBot) {
      rows.push(SupportedPlatform.LinkedIn);
    }
    if (this.facebookLinkBot) {
      rows.push(SupportedPlatform.FacebookMessenger);
    }
    if (this.googleLinkBot) {
      rows.push(SupportedPlatform.GoogleBusiness);
    }
    return rows;
  }

  /** Title is only shown when a selected platform uses a separate title (not Google/IG/FB/LinkedIn). */
  get showTitleField(): boolean {
    if (!this.selectedPlatforms.length) {
      return true;
    }
    const textOnlyPlatforms = new Set<SupportedPlatform>([
      SupportedPlatform.FacebookMessenger,
      SupportedPlatform.GoogleBusiness,
      SupportedPlatform.Instagram,
      SupportedPlatform.LinkedIn,
    ]);
    return this.selectedPlatforms.some((platform) => !textOnlyPlatforms.has(platform));
  }

  /** True when either image or video is selected in media uploader. */
  get hasMediaSelected(): boolean {
    return !!(this.postVideo || this.postImageHorizontal || this.postImageSquare);
  }

  get callToActionTitle(): string {
    return this.postData.callToAction?.title ?? '';
  }
  set callToActionTitle(v: string) {
    this.ensureCallToAction();
    this.postData.callToAction!.title = v;
  }
  get callToActionUrl(): string {
    return this.postData.callToAction?.url ?? '';
  }
  set callToActionUrl(v: string) {
    this.ensureCallToAction();
    this.postData.callToAction!.url = v;
  }

  ensureCallToAction() {
    if (!this.postData.callToAction) {
      this.postData.callToAction = { title: '', url: '' };
    }
  }

  mounted() {
    if (this.chain) {
      this.selectedBots = this.chain.bots?.slice() || [];
    }
    const draft = this.type === 'bot' ? takeCrossPostDraft(this.bot?.id) : null;
    if (draft) {
      void this.applyCrossPostDraft(draft);
      return;
    }
    this.syncSelectedPlatformsWithEnabled();
  }

  /** Prefills compose from a Feed cross-post draft (title, content, media, platforms). */
  async applyCrossPostDraft(draft: CrossPostDraft) {
    this.preserveCrossPostPrefill = true;
    const enabled = this.enabledPostingPlatforms.map((p) => p.value);
    this.selectedPlatforms = draft.selectedPlatforms.filter((platform) =>
      enabled.includes(platform),
    );
    this.postData = {
      ...this.resetValue(),
      title: draft.title,
      content: draft.content,
    };
    this.postImageHorizontal = draft.images.horizontal;
    this.postImageSquare = draft.images.square;
    this.postVideo = draft.video;
    this.videoDurationSeconds = null;
    this.mediaDropzoneKey++;
    await this.$nextTick();

    const file = draft.video || draft.images.horizontal || draft.images.square;
    if (!file) {
      return;
    }
    const kind = draft.video ? 'video' : 'image';
    const mdz = this.$refs.mediaDropzone as PostMediaDropzone | undefined;
    mdz?.setImportedFile(file, kind);
    this.onMediaInput({ kind, file });
  }

  async onSubmit() {
    if (!this.canSubmit) {
      return;
    }
    this.isSubmitting = true;
    try {
      const resolvedTitle = this.showTitleField
        ? this.postData.title
        : (this.postData.content || '').trim().slice(0, 120);
      const dataWithPlatforms = {
        ...this.postData,
        title: resolvedTitle,
        selectedPlatforms: this.selectedPlatforms.slice(),
      } as PublishPost & { selectedPlatforms: SupportedPlatform[] };
      const publishOptions: PublishPostOptions = {
        id: this.id,
        data: dataWithPlatforms,
        images: {
          horizontal: this.postImageHorizontal,
          square: this.postImageSquare,
        },
        video: this.postVideo,
      };
      const result = await (this.type === 'bot'
        ? PostApi.publishBotPost(publishOptions)
        : PostApi.publishChainPost({
            ...publishOptions,
            botIds: this.selectedBots.map((bot) => bot.id),
          }));

      this.$emit('publish', result);
      const linkedInPostId =
        this.type === 'bot' &&
        !Array.isArray(result) &&
        this.selectedPlatforms.includes(SupportedPlatform.LinkedIn) &&
        result.publishInfo?.linkedIn
          ? result.publishInfo.linkedIn
          : null;
      if (linkedInPostId) {
        const linkedInUrl = `https://www.linkedin.com/feed/update/${linkedInPostId}`;
        this.$notify.success({
          title: 'Successfully published post',
          description: `LinkedIn post created. Open it directly or find it under “Beiträge Ihrer Unternehmensseite” in LinkedIn admin (not “Feed”). ${linkedInUrl}`,
        });
      } else {
        this.$notify.success('Successfully published post');
      }
      this.preserveCrossPostPrefill = false;
      this.reset();
    } catch (e) {
      this.$notify.error({
        title: 'Publishing post failed',
        description: describePostPublishError(e, GENERIC_ERROR_DESCRIPTION),
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  onRemoveBot(bot: Bot) {
    this.selectedBots = this.selectedBots.filter((b: Bot) => b.id !== bot.id);
  }

  onResetBots() {
    this.selectedBots = this.chain.bots?.slice() || [];
  }

  get hasRemoved() {
    return this.selectedBots.length !== this.chain.bots?.length;
  }

  /** Returns true when platform is currently selected for posting. */
  isPlatformSelected(platform: SupportedPlatform): boolean {
    return this.selectedPlatforms.includes(platform);
  }

  /** Toggles one platform selection from the picker checkbox. */
  onPlatformToggle(platform: SupportedPlatform, checked: boolean) {
    if (checked) {
      if (!this.selectedPlatforms.includes(platform)) {
        this.selectedPlatforms = [...this.selectedPlatforms, platform];
      }
      return;
    }
    this.selectedPlatforms = this.selectedPlatforms.filter((p) => p !== platform);
  }

  /** Handles checkbox DOM event and delegates to platform toggle method. */
  onPlatformCheckboxChange(platform: SupportedPlatform, event: Event) {
    const target = event.target as HTMLInputElement | null;
    this.onPlatformToggle(platform, !!target?.checked);
  }

  /** Keeps picker selection aligned with currently enabled platforms; defaults to all enabled. */
  syncSelectedPlatformsWithEnabled() {
    const enabled = this.enabledPostingPlatforms.map((p) => p.value);
    const selected = this.selectedPlatforms.filter((p) => enabled.includes(p));
    if (this.preserveCrossPostPrefill) {
      this.selectedPlatforms = selected;
      return;
    }
    for (const platform of enabled) {
      if (!selected.includes(platform)) {
        selected.push(platform);
      }
    }
    this.selectedPlatforms = selected;
  }

  onMediaInput(payload: PostMediaInputPayload) {
    if (!payload.file) {
      this.postImageHorizontal = null;
      this.postImageSquare = null;
      this.postVideo = null;
      this.videoDurationSeconds = null;
      return;
    }
    if (payload.kind === 'video') {
      this.postVideo = payload.file;
      this.postImageHorizontal = null;
      this.postImageSquare = null;
      if (payload.durationSeconds !== undefined) {
        this.videoDurationSeconds = payload.durationSeconds;
      }
      return;
    }
    this.postImageHorizontal = payload.file;
    this.postImageSquare = payload.file;
    this.postVideo = null;
    this.videoDurationSeconds = null;
  }

  /** Opens import modal when any supported platform is connected. */
  onOpenInstagramPostsPicker() {
    if (!this.importablePlatforms.length) {
      this.$notify.error({
        title: 'Import unavailable',
        description:
          'Connect Instagram, LinkedIn, Facebook or Google Business for one of the selected bots to import posts.',
      });
      return;
    }
    this.showImportModal = true;
  }

  async onSelectImportItem(item: ImportFeedItem) {
    const isTextOnlyImport =
      item.platform === SupportedPlatform.FacebookMessenger ||
      item.platform === SupportedPlatform.GoogleBusiness;
    const title = (item.title || '').trim();
    const content = (item.content || '').trim();
    this.postData.title = isTextOnlyImport ? '' : title;
    this.postData.content = content || title;
    this.postImageHorizontal = null;
    this.postImageSquare = null;
    this.postVideo = null;
    this.videoDurationSeconds = null;
    this.mediaDropzoneKey++;
    await this.$nextTick();

    if (item.platform === SupportedPlatform.Instagram) {
      const sourceBot = this.instagramLinkBot;
      if (!sourceBot) {
        return;
      }
      try {
        const { file, kind } = await InstagramApi.downloadMediaFile(sourceBot.id, item.id);
        const treatAsVideo =
          kind === 'video' || String(item.mediaType || '').toUpperCase() === 'VIDEO';
        await this.$nextTick();
        const mdz = this.$refs.mediaDropzone as PostMediaDropzone;
        mdz.setImportedFile(file, treatAsVideo ? 'video' : 'image');
        this.onMediaInput({ kind: treatAsVideo ? 'video' : 'image', file });
      } catch (e) {
        this.$notify.error({
          title: 'Could not load Instagram media',
          description: GENERIC_ERROR_DESCRIPTION,
        });
      }
      return;
    }

    if (item.platform === SupportedPlatform.LinkedIn) {
      const sourceBot = this.linkedInLinkBot;
      if (!sourceBot || !item.id) {
        return;
      }
      if (!item.thumbnailUrl && String(item.mediaType || '').toUpperCase() === 'TEXT') {
        return;
      }
      try {
        const { file, kind } = await LinkedInApi.downloadMediaFile(sourceBot.id, item.id);
        const treatAsVideo =
          kind === 'video' || String(item.mediaType || '').toUpperCase() === 'VIDEO';
        await this.$nextTick();
        const mdz = this.$refs.mediaDropzone as PostMediaDropzone;
        mdz.setImportedFile(file, treatAsVideo ? 'video' : 'image');
        this.onMediaInput({ kind: treatAsVideo ? 'video' : 'image', file });
      } catch (e) {
        this.$notify.error({
          title: 'Could not load LinkedIn media',
          description: extractApiErrorMessage(e, GENERIC_ERROR_DESCRIPTION),
        });
      }
      return;
    }

    if (item.platform === SupportedPlatform.GoogleBusiness) {
      const sourceBot = this.googleLinkBot;
      if (!sourceBot || !item.id) {
        return;
      }
      if (!item.thumbnailUrl && String(item.mediaType || '').toUpperCase() === 'TEXT') {
        return;
      }
      try {
        const { file, kind } = await GoogleMyBusinessApi.downloadMediaFile(sourceBot.id, item.id);
        await this.$nextTick();
        const mdz = this.$refs.mediaDropzone as PostMediaDropzone;
        mdz.setImportedFile(file, kind);
        this.onMediaInput({ kind, file });
      } catch (e) {
        this.$notify.error({
          title: 'Could not load Google Business media',
          description: extractApiErrorMessage(e, GENERIC_ERROR_DESCRIPTION),
        });
      }
    }
  }

  @Watch('bot', { deep: true })
  @Watch('chain', { deep: true })
  @Watch('selectedBots', { deep: true })
  onPlatformContextChanged() {
    this.syncSelectedPlatformsWithEnabled();
  }

  @Watch('bot', { deep: true })
  @Watch('chain', { deep: true })
  reset() {
    if (this.preserveCrossPostPrefill) {
      this.syncSelectedPlatformsWithEnabled();
      return;
    }
    this.postData = this.resetValue();
    this.postImageHorizontal = null;
    this.postImageSquare = null;
    this.postVideo = null;
    this.videoDurationSeconds = null;
    this.showImportModal = false;
    this.mediaDropzoneKey++;
    this.syncSelectedPlatformsWithEnabled();
  }

  resetValue(): PublishPost {
    return {
      title: '',
      content: '',
      callToAction: {
        title: '',
        url: '',
      },
    };
  }
}
